import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { getMediaClient } from '@atlaskit/media-client-react/get-media-client';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	buildImageUrl,
	IMAGE_CREATE_KEY,
	isImageCreateData,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { fetchMauiPersonalReadToken } from '@atlassian/conversation-assistant-service/common/utils/maui-media-token';
import {
	type AssistanceServiceInjectedParams,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { isStaging } from '@atlassian/conversation-assistant-viz/utils/env-helpers';

import type {
	AIHeaderImageStatus,
	GenerateImageProps,
	UseRemixImageGenerationProps,
	UseRemixImageGenerationReturn,
} from '../types';

const PRODUCT = 'confluence';

type RemixStreamMessage = {
	message?: {
		content?: string;
		message?: {
			actions?: Array<{ data?: unknown; key?: string }>;
		};
	};
	type?: string;
};

type ActiveGeneration = {
	controller: AbortController;
	notifyAbort: () => void;
};

const unwrapStreamMessage = (value: unknown): RemixStreamMessage => {
	if (
		typeof value === 'object' &&
		value !== null &&
		'__isSSEWrapper' in value &&
		value.__isSSEWrapper === true &&
		'data' in value
	) {
		return value.data as RemixStreamMessage;
	}

	return value as RemixStreamMessage;
};

const getGeneratedMediaFileId = async (generator: AsyncIterable<unknown>): Promise<string> => {
	for await (const value of generator) {
		const streamMessage = unwrapStreamMessage(value);

		if (streamMessage.type === 'ERROR') {
			throw new Error(streamMessage.message?.content ?? 'Remix image generation failed');
		}

		if (streamMessage.type === 'FINAL_RESPONSE') {
			const imageAction = streamMessage.message?.message?.actions?.find(
				(action) => action.key === IMAGE_CREATE_KEY,
			);
			if (!isImageCreateData(imageAction?.data)) {
				throw new Error('Remix did not return a generated image');
			}

			return imageAction.data.mediaFileId;
		}
	}

	throw new Error('Remix image generation ended without a response');
};

const useRemixImageGenerationWithAssistanceService = ({
	contentId,
	createEditorMediaProvider,
	eventCallbacks,
	successfulGenerationStatus = 'SUCCESSFULLY_SET',
	userId,
	assistanceServiceInjectedParams: { assistanceService },
}: UseRemixImageGenerationProps &
	AssistanceServiceInjectedParams): UseRemixImageGenerationReturn => {
	const [aiHeaderImageStatus, setAIHeaderImageStatus] =
		useState<AIHeaderImageStatus>('NOT_LOADING');
	const [aiHeaderImageFileId, setAIHeaderImageFileId] = useState<string>();
	const [error, setError] = useState<string | null>(null);
	const activeGenerationRef = useRef<ActiveGeneration>();
	const generationSequenceRef = useRef(0);

	useEffect(
		() => () => {
			generationSequenceRef.current += 1;
			const activeGeneration = activeGenerationRef.current;
			activeGenerationRef.current = undefined;
			activeGeneration?.controller.abort();
			activeGeneration?.notifyAbort();
		},
		[],
	);

	const uploadGeneratedImage = useCallback(
		async (mediaFileId: string): Promise<string> => {
			if (!userId) {
				throw new Error('Cannot upload a Remix image without a user ID');
			}
			if (!createEditorMediaProvider) {
				throw new Error('Cannot upload a Remix image without an editor media provider');
			}

			const mediaProvider = await createEditorMediaProvider({
				contentId,
				contentType: 'page',
				isExternalShare: false,
			});
			const targetCollection = mediaProvider.uploadParams?.collection;
			if (!mediaProvider.uploadMediaClientConfig || !targetCollection) {
				throw new Error('Editor media provider is missing upload configuration');
			}

			const { token, collection } = await fetchMauiPersonalReadToken(PRODUCT);
			const sourceUrl = buildImageUrl({
				fileId: mediaFileId,
				token: token.split('Bearer ')[1],
				collection,
				clientId: userId,
				isStaging: isStaging(),
			});
			const mediaClient = getMediaClient(mediaProvider.uploadMediaClientConfig);
			const result = await mediaClient.file.uploadExternal(
				sourceUrl,
				targetCollection,
				undefined,
				true,
			);

			return result.uploadableFileUpfrontIds.id;
		},
		[contentId, createEditorMediaProvider, userId],
	);

	const generateImage = useCallback(
		async ({
			adfContent,
			aspectRatio,
			imageGenerationModel,
			showAcceptanceFlow = false,
			theme,
			useRawPrompt = false,
		}: GenerateImageProps): Promise<string | undefined> => {
			const generationSequence = ++generationSequenceRef.current;
			const previousGeneration = activeGenerationRef.current;
			previousGeneration?.controller.abort();
			previousGeneration?.notifyAbort();

			const conversationId = crypto.randomUUID();
			const abortController = new AbortController();
			const eventParams = {
				contentId,
				useRawPrompt,
				...(theme && { theme }),
				...(aspectRatio && { aspectRatio }),
				...(imageGenerationModel && { imageGenerationModel }),
			};
			let abortNotified = false;
			const notifyAbort = () => {
				if (!abortNotified) {
					abortNotified = true;
					eventCallbacks?.onGenerateAbort?.(eventParams);
				}
			};
			activeGenerationRef.current = {
				controller: abortController,
				notifyAbort,
			};

			setAIHeaderImageFileId(undefined);
			setError(null);
			setAIHeaderImageStatus(showAcceptanceFlow ? 'LOADING_WITH_ACCEPTANCE_FLOW' : 'LOADING');
			eventCallbacks?.onGenerateStart?.(eventParams);

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [{ type: 'text', text: adfContent }],
					},
				],
			};
			const dynamicUiSource = {
				contentType: 'page',
				startLocalId: '',
				endLocalId: '',
				startIndex: 0,
				endIndex: -1,
				fragmentAdf: prompt.content,
				fullAdf: prompt,
			};

			try {
				const creationContext = {
					dynamicUiType: 'image',
					dynamicUiSubtype: theme ?? 'image',
					dynamicUiSource,
					source: 'remix',
					isStagingArea: true,
					productKey: PRODUCT,
					isViewMode: false,
					...(expValEquals('rovo_remix_experience_context', 'isEnabled', true)
						? { experience: 'remix', blocks: ['image'] }
						: {}),
					...(aspectRatio ? { additionalContext: { aspectRatio } } : {}),
				};
				const { generator } = await assistanceService.sendMessageStream({
					message: prompt,
					conversationId,
					controller: abortController,
					storeMessage: false,
					citationsEnabled: true,
					optInSSEStream: true,
					additionalContext: {
						creationContext,
						userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
				});
				const temporaryMediaFileId = await getGeneratedMediaFileId(generator);
				const generatedImageId = await uploadGeneratedImage(temporaryMediaFileId);

				if (
					abortController.signal.aborted ||
					generationSequence !== generationSequenceRef.current
				) {
					notifyAbort();
					return successfulGenerationStatus === 'NOT_LOADING' ? undefined : generatedImageId;
				}

				setAIHeaderImageFileId(generatedImageId);
				setAIHeaderImageStatus(successfulGenerationStatus);
				eventCallbacks?.onGenerateSuccess?.(eventParams);
				return generatedImageId;
			} catch (generationError) {
				const normalizedError =
					generationError instanceof Error ? generationError : new Error('Unknown error occurred');
				const isCurrentGeneration = generationSequence === generationSequenceRef.current;
				if (
					abortController.signal.aborted ||
					normalizedError.name === 'AbortError' ||
					!isCurrentGeneration
				) {
					notifyAbort();
					if (isCurrentGeneration) {
						if (successfulGenerationStatus === 'NOT_LOADING') {
							setAIHeaderImageStatus('NOT_LOADING');
						} else {
							// Preserve the existing default Remix behaviour for non-header consumers.
							setError(normalizedError.message);
							setAIHeaderImageStatus('ERROR');
						}
					}
					return undefined;
				}

				setError(normalizedError.message);
				setAIHeaderImageStatus('ERROR');
				eventCallbacks?.onGenerateError?.({ ...eventParams, error: normalizedError });
				return undefined;
			} finally {
				if (activeGenerationRef.current?.controller === abortController) {
					activeGenerationRef.current = undefined;
				}
			}
		},
		[
			assistanceService,
			contentId,
			eventCallbacks,
			successfulGenerationStatus,
			uploadGeneratedImage,
		],
	);

	// Begin the loading experience before an AIFC title is available to generate from.
	const fakeGenerateImage = useCallback(() => {
		setAIHeaderImageFileId(undefined);
		setError(null);
		setAIHeaderImageStatus('LOADING');
	}, []);

	return {
		generateImage,
		fakeGenerateImage,
		loading:
			aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW',
		error,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		setAIHeaderImageStatus,
	};
};

const useRemixImageGenerationWithService = createHookWithAssistanceService(
	useRemixImageGenerationWithAssistanceService,
);

export const useRemixImageGeneration = (
	props: UseRemixImageGenerationProps,
): UseRemixImageGenerationReturn =>
	useRemixImageGenerationWithService({
		...props,
		assistanceServiceParams: {
			product: PRODUCT,
			experienceId: 'ai-mate',
			siteId: props.cloudId,
			touchpointSource: props.touchpointSource ?? 'company-hub-image-generation',
		},
	});
